'use client';

import React, { useState } from 'react';
import { Building2, Shield, Users, SkipForward, Settings } from 'lucide-react';
import { DomainSetup } from './domain-setup';
import { SSOConfiguration } from './sso-configuration';
import { AdminSetup } from './admin-setup';
import { OrganizationSettings } from './organization-settings';
import { Verification } from './verification';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme-context';
import { getBackgroundClasses, getTextClasses, getBorderClasses } from '@/lib/theme-utils';

type EnrollmentStep = 'domain' | 'sso-config' | 'admin-setup' | 'settings' | 'verification';

interface TenantData {
  domain: string;
  organizationName: string;
  ssoConfig: any;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  adminPassword: string;
  settings: {
    timezone: string;
    defaultLanguage: string;
    notificationPreferences: {
      emailAlerts: boolean;
      smsAlerts: boolean;
    };
  };
}

export default function TenantEnrollment({ prefillDomain = '' }: { prefillDomain?: string }) {
  const { theme } = useTheme();
  const [step, setStep] = useState<EnrollmentStep>('domain');
  const [tenantData, setTenantData] = useState<TenantData>({
    domain: prefillDomain,
    organizationName: '',
    ssoConfig: null,
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    adminPassword: '',
    settings: {
      timezone: 'America/New_York',
      defaultLanguage: 'en',
      notificationPreferences: {
        emailAlerts: true,
        smsAlerts: false
      }
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { user } = useAuth(); // Get user from auth context

  // Extract user info from JWT for pre-filling
  const getUserInfoFromJWT = () => {
    if (!user?.profile) return { firstName: '', lastName: '', email: '' };
    
    return {
      firstName: user.profile.given_name || '',
      lastName: user.profile.family_name || '',
      email: user.profile.email || ''
    };
  };

  const handleDomainComplete = (domain: string, organizationName: string) => {
    setTenantData(prev => ({ ...prev, domain, organizationName }));
    setStep('sso-config');
  };

  const handleSSOComplete = (ssoConfig: any) => {
    setTenantData(prev => ({ ...prev, ssoConfig }));
    setStep('admin-setup');
  };

  const handleSkipSSO = () => {
    // Set local authentication config
    setTenantData(prev => ({ 
      ...prev, 
      ssoConfig: {
        ssoProviderType: 'local',
        providerConfig: {}
      }
    }));
    setStep('admin-setup');
  };

  const handleAdminComplete = (adminEmail: string, adminFirstName: string, adminLastName: string, adminPassword: string) => {
    setTenantData(prev => ({ 
      ...prev, 
      adminEmail, 
      adminFirstName, 
      adminLastName, 
      adminPassword 
    }));
    setStep('settings');
  };

  const handleSettingsComplete = (settings: any) => {
    setTenantData(prev => ({ ...prev, settings }));
    setStep('verification');
  };

  const handleSkipSettings = () => {
    setStep('verification');
  };

  const handleVerificationComplete = () => {
    setIsLoading(true);
    // Redirect to success page or login
    setTimeout(() => {
      router.push('/enrollment-success');
    }, 2000);
  };

  const getStepStatus = (stepName: EnrollmentStep) => {
    const stepOrder = ['domain', 'sso-config', 'admin-setup', 'settings', 'verification'];
    const currentIndex = stepOrder.indexOf(step);
    const stepIndex = stepOrder.indexOf(stepName);
    
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black to-slate-900 flex items-center justify-center p-4">
      <div className={`${getBackgroundClasses(theme, 'card')} backdrop-blur-sm rounded-xl shadow-xl max-w-4xl w-full p-6 border ${getBorderClasses(theme)}`}>
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mb-3">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <h1 className={`text-2xl font-bold ${getTextClasses(theme)} mb-1`}>
            Set Up Your Organization
          </h1>
          <p className={`${getTextClasses(theme, 'muted')} text-sm`}>
            Configure your organization's domain and authentication settings
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-6">
          <div className="flex items-center justify-center space-x-3">
            {[
              { name: 'Domain', icon: Building2, step: 'domain' as EnrollmentStep },
              { name: 'Auth Setup', icon: Shield, step: 'sso-config' as EnrollmentStep },
              { name: 'Admin Setup', icon: Users, step: 'admin-setup' as EnrollmentStep },
              { name: 'Settings', icon: Settings, step: 'settings' as EnrollmentStep },
              { name: 'Verification', icon: Shield, step: 'verification' as EnrollmentStep }
            ].map((stepInfo, index) => {
              const status = getStepStatus(stepInfo.step);
              const Icon = stepInfo.icon;
              
              return (
                <React.Fragment key={stepInfo.step}>
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center transition-all
                      ${status === 'completed' ? '' : 
                        status === 'current' ? 'bg-cyan-500' : getBackgroundClasses(theme, 'card')}
                    `}>
                      <Icon className={`w-5 h-5 ${
                        status === 'completed' ? 'text-white' : 
                        status === 'current' ? 'text-white' : getTextClasses(theme, 'muted')
                      }`} />
                    </div>
                    <span className={`text-xs mt-1 ${
                      status === 'completed' ? '' : 
                      status === 'current' ? 'text-cyan-400' : getTextClasses(theme, 'muted')
                    }`}>
                      {stepInfo.name}
                    </span>
                  </div>
                  
                  {index < 4 && (
                    <div className={`
                      w-12 h-0.5 transition-all
                      ${status === 'completed' ? 'bg-green-500' : getBackgroundClasses(theme, 'card')}
                    `} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="min-h-[350px]">
          {step === 'domain' && (
            <DomainSetup 
              onComplete={handleDomainComplete}
              isLoading={isLoading}
              prefillDomain={prefillDomain}
            />
          )}
          
          {step === 'sso-config' && (
            <div>
              <SSOConfiguration 
                domain={tenantData.domain}
                organizationName={tenantData.organizationName}
                onComplete={handleSSOComplete}
                onBack={() => setStep('domain')}
                isLoading={isLoading}
              />
              
              {/* Skip SSO Option */}
              <div className="mt-4 text-center">
                <div className={`border-t ${getBorderClasses(theme)} pt-4`}>
                  <p className={`text-xs ${getTextClasses(theme, 'muted')} mb-3`}>
                    Want to get started quickly?
                  </p>
                  <button
                    onClick={handleSkipSSO}
                    className={`inline-flex items-center px-3 py-2 text-xs font-medium ${getTextClasses(theme)} ${getBackgroundClasses(theme, 'default')} border ${getBorderClasses(theme)} rounded-lg transition-colors`}
                  >
                    <SkipForward className="w-3 h-3 mr-1" />
                    Skip SSO for now
                  </button>
                  <p className={`text-xs ${getTextClasses(theme, 'muted')} mt-1`}>
                    You can set up SSO later from your organization settings
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {step === 'admin-setup' && (
            <AdminSetup 
              domain={tenantData.domain}
              organizationName={tenantData.organizationName}
              onComplete={handleAdminComplete}
              onBack={() => setStep('sso-config')}
              isLoading={isLoading}
              prefillData={getUserInfoFromJWT()} // Pass JWT user info
            />
          )}
          
          {step === 'settings' && (
            <OrganizationSettings
              settings={tenantData.settings}
              onComplete={handleSettingsComplete}
              onBack={() => setStep('admin-setup')}
              onSkip={handleSkipSettings}
              isLoading={isLoading}
            />
          )}
          
          {step === 'verification' && (
            <Verification 
              tenantData={tenantData}
              onComplete={handleVerificationComplete}
              onBack={() => setStep('settings')}
              isLoading={isLoading}
            />
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
            Need help? Contact our support team for assistance.
          </p>
        </div>
      </div>
    </div>
  );
} 